'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { GlobalSearchDialog } from './global-search-dialog';

interface GlobalSearchContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  /**
   * Open the overlay pre-seeded with a query — the top-bar input hands off
   * the characters the user already typed so nothing is lost.
   */
  openWithQuery: (query: string) => void;
}

const GlobalSearchContext = createContext<GlobalSearchContextValue | null>(
  null,
);

/**
 * App-wide owner of the global-search overlay. Mounted once in `RootProviders`
 * so there is exactly ONE Cmd+K / Ctrl+K listener and ONE dialog instance, no
 * matter how many `GlobalSearchTrigger`s the header surfaces render. (Mounting
 * the listener per-trigger would open every dialog at once.)
 */
export function GlobalSearchProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');

  // Global keyboard shortcut (R1). Lifted from `useCommandPalette`, but mounted
  // for every user — not just super admins — via the provider.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Plain ⌘K only — ⌘⇧K is reserved for the super-admin navigation
      // palette (useCommandPalette), so the two overlays never stack.
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && e.key === 'k') {
        // Surfaces with the inline top-bar search (GlobalSearchInline) own
        // ⌘K themselves — it focuses the inline input. Only fall back to
        // this dialog where no inline bar is mounted (marketing header,
        // bare 404).
        if (document.querySelector('[data-global-search-inline]')) return;
        e.preventDefault();
        setOpen((prev) => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  const openWithQuery = useCallback((query: string) => {
    setInitialQuery(query);
    setOpen(true);
  }, []);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setInitialQuery('');
  }, []);

  const value = useMemo(
    () => ({ open, setOpen: handleOpenChange, openWithQuery }),
    [open, handleOpenChange, openWithQuery],
  );

  return (
    <GlobalSearchContext.Provider value={value}>
      {children}
      <GlobalSearchDialog
        open={open}
        onOpenChange={handleOpenChange}
        initialQuery={initialQuery}
      />
    </GlobalSearchContext.Provider>
  );
}

/** Open/close handle for the shared global-search overlay. */
export function useGlobalSearch(): GlobalSearchContextValue {
  const ctx = useContext(GlobalSearchContext);
  if (!ctx) {
    throw new Error(
      'useGlobalSearch must be used within a GlobalSearchProvider',
    );
  }
  return ctx;
}

/**
 * Non-throwing variant of {@link useGlobalSearch}. Returns `null` when no
 * `GlobalSearchProvider` is an ancestor instead of throwing.
 *
 * Some search triggers (e.g. the marketing `SiteHeader`) are rendered on
 * surfaces that sit OUTSIDE the app-shell provider tree — most notably the
 * root `not-found.tsx`, which renders `SiteHeader` for a bare 404. A `POST`
 * to a page with no server action (e.g. `POST ballee.co/`) resolves to that
 * not-found boundary and server-renders the trigger with no provider in
 * scope, so the throwing hook crashed the render (Sentry BALLEE-EZ).
 *
 * Mirrors the `useFavoritesOptional` precedent: a trigger that can mount
 * without its provider should degrade to a no-op, not throw.
 */
export function useGlobalSearchOptional(): GlobalSearchContextValue | null {
  return useContext(GlobalSearchContext);
}
